// @flow
import React, { PureComponent } from "react";
import { noop, get } from "lodash";
import mediator from "@tvg/mediator";
import type { BinaryFn, NullaryFn } from "@tvg/types/Functional";
import type { Device } from "@tvg/types/Device";
import type { TutorialSlide } from "@tvg/types/OnBoarding";
import Icon from "@fdr/static-ui/Icons";
import { close } from "@fdr/static-ui/Icons/icons";
import buildColor from "@fdr/static-ui/ColorPalette";

import Button from "../../_atoms/Button";
import TextHighlighted from "../../_atoms/TextHighlighted";
import Title from "../../_atoms/Title";
import Pagination from "../../_molecules/Pagination";
import {
  TutorialMainWrapper,
  MainPanelWrapper,
  TutorialSlidePanel,
  Description,
  CloseButton,
  ImageWrapper,
  TutorialPaginationWrapper,
  TutorialSlidesContainer,
  TutorialSlideItem,
  TutorialBottomControls,
  TutorialWrapper
} from "./styled-components";

export type Props = {
  /**
   * Flag indicating if we have a tutorial or a prompt
   */
  isPrompt: boolean,
  /**
   * Flag indicating if we have a tutorial or a prompt
   */
  isNew: boolean,
  /**
   * Highlighted text content (empty string for none)
   */
  highlightedText: string,
  /**
   * Tutorial structure
   */
  tutorials: TutorialSlide[],
  /**
   * Label that appears on list
   */
  listButtonLabel: string,
  /**
   * Navigation for bottom button on list
   */
  listButtonCallback: NullaryFn<void>,
  /**
   * Label that appears on tutorial
   */
  buttonLabel: string,
  /**
   * Navigation for bottom button on tutorial
   */
  buttonNav: string,
  /**
   * Label that appears on prompt
   */
  promptLabel: string,
  /**
   * Callback for bottom button on prompt
   */
  promptClick: NullaryFn<mixed>,
  /**
   * Callback for closing the panel
   */
  onClose: NullaryFn<mixed>,
  /**
   * Callback for clicking the final button on the tutorial steps
   */
  finalStepButtonClick: NullaryFn<mixed>,
  /**
   * The device this is showing in
   */
  device: Device,
  /**
   * Callback for when the back page paginator gets clicked
   */
  onBackPage: BinaryFn<number, number, mixed>,
  /**
   * Callback for when the forward page paginator gets clicked
   */
  onForwardPage: BinaryFn<number, number, mixed>,
  /**
   * Active Tutorial title
   */
  tutorialTitle: string
};

type State = {
  currentSlide: number,
  totalSlides: number
};

export default class TutorialPanel extends PureComponent<Props, State> {
  static defaultProps = {
    isPrompt: false,
    isNew: false,
    tutorialTitle: "",
    highlightedText: "New feature",
    tutorials: [],
    promptLabel: "Find out more",
    buttonLabel: "Done",
    listButtonLabel: "Find out more",
    buttonNav: "",
    promptClick: noop,
    listButtonCallback: noop,
    onClose: noop,
    finalStepButtonClick: noop,
    device: "mobile",
    onBackPage: noop,
    onForwardPage: noop
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      currentSlide: 0,
      totalSlides: get(this.props, "tutorials.length", 1)
    };
  }

  componentDidMount() {
    mediator.base.dispatch({
      type: "FDR_TUTORIALS:LOAD_TUTORIAL",
      payload: {
        title: this.props.tutorialTitle,
        tutorialCategory: this.props.tutorialTitle,
        pageNumber: this.state.currentSlide + 1,
        totalPages: this.state.totalSlides
      }
    });
  }

  static getDerivedStateFromProps = (nextProps: Props, prevState: State) => {
    const totalSlides = nextProps.tutorials.length;
    const prevTotalSlides = prevState.totalSlides;
    return prevTotalSlides !== totalSlides
      ? {
          totalSlides
        }
      : null;
  };

  onBackPage = () => {
    const prevPage = this.state.currentSlide - 1;
    const current = prevPage >= 0 ? prevPage : 0;
    this.setState({
      currentSlide: current
    });
    this.props.onBackPage(current + 1, this.state.totalSlides);
  };

  onForwardPage = () => {
    const { totalSlides, currentSlide } = this.state;
    const nextPage = currentSlide + 1;
    const current = nextPage <= totalSlides ? nextPage : totalSlides;

    this.setState({
      currentSlide: current
    });

    this.props.onForwardPage(current + 1, totalSlides);
  };

  bottomRender = () => {
    const { isPrompt } = this.props;

    return isPrompt ? this.renderBottomButton() : this.renderTutorialNav();
  };

  goToTutorial = () => {
    // @TODO: Temporarily commented, but will be useful to differentiate clicks for GTM
    // const { promptClick, isPrompt } = this.props;
    const { promptClick } = this.props;
    promptClick();
  };

  renderBottomButton = () => {
    const { promptLabel } = this.props;

    return (
      <Button
        size="big"
        type="secondary"
        text={promptLabel}
        className="finalTutorialButton"
        qaLabel="tutorialPanel-promptButton"
        onClick={this.goToTutorial}
        isStretched
      />
    );
  };

  renderTutorialNav = () => {
    const { tutorials, buttonNav, buttonLabel, finalStepButtonClick } =
      this.props;
    const { currentSlide } = this.state;
    const totalPages = tutorials.length;
    const finalStepInfo = {
      buttonNav,
      buttonLabel,
      finalStepButtonClick,
      qaLabel: "tutorialPanel-finalButton"
    };

    return (
      <TutorialPaginationWrapper>
        <Pagination
          totalPages={totalPages}
          pageSelected={currentSlide + 1}
          onForwardPage={this.onForwardPage}
          onBackPage={this.onBackPage}
          finalStepInfo={finalStepInfo}
          theme="tutorials"
        />
      </TutorialPaginationWrapper>
    );
  };

  renderCloseButton = () => (
    <CloseButton qaLabel="onboarding-closeBtn" onClick={this.props.onClose}>
      <Icon icon={close} color={buildColor("grey", "700")} size={16} />
    </CloseButton>
  );

  render() {
    const { highlightedText, tutorials, isPrompt, isNew, device } = this.props;
    return (
      <TutorialMainWrapper
        isPrompt={isPrompt}
        device={device}
        data-qa-label={isPrompt ? "onboardingPrompt" : "onboardingTutorial"}
      >
        <TutorialWrapper>
          <TutorialSlidesContainer
            disabled={isPrompt}
            index={this.state.currentSlide}
            onChangeIndex={(a, b) =>
              a > b ? this.onForwardPage() : this.onBackPage()
            }
          >
            {tutorials.map((tutorialSlide, index) => (
              <TutorialSlideItem
                visible={index === this.state.currentSlide}
                key={`tutorialSlide_${index.toString()}`}
                data-qa-label={`tutorialSlide_${index.toString()}`}
              >
                <ImageWrapper
                  isPrompt
                  data-qa-label={`onboarding-image${index + 1}`}
                  style={
                    isPrompt
                      ? { backgroundImage: `url('${tutorialSlide.image}')` }
                      : null
                  }
                >
                  {!isPrompt && (
                    <img
                      src={tutorialSlide.image}
                      alt={`tutorial slide number ${index + 1}`}
                    />
                  )}
                </ImageWrapper>
                <MainPanelWrapper>
                  <div>
                    {isPrompt && this.renderCloseButton()}
                    {highlightedText && isNew && (
                      <TextHighlighted bold type="marketing">
                        {highlightedText}
                      </TextHighlighted>
                    )}
                    <TutorialSlidePanel>
                      <Title
                        uppercase={false}
                        fontSize={18}
                        className="mainTitle"
                        qaLabel={`onboarding-slideTitle_${index.toString()}`}
                        color={buildColor("navy", "700")}
                      >
                        {tutorialSlide.title}
                      </Title>
                      <Description
                        data-qa-label={`onboarding-slideDescription_${index.toString()}`}
                      >
                        {tutorialSlide.description}
                      </Description>
                    </TutorialSlidePanel>
                  </div>
                </MainPanelWrapper>
              </TutorialSlideItem>
            ))}
          </TutorialSlidesContainer>
          <TutorialBottomControls>{this.bottomRender()}</TutorialBottomControls>
        </TutorialWrapper>
      </TutorialMainWrapper>
    );
  }
}
